import React, { useState } from 'react';
import { Modal, Button, Carousel } from 'react-bootstrap';
import './EvidenceGalleryModal.scss';

const EvidenceGalleryModal = ({
  show,
  onHide,
  files = [],
  fileURLs = [],
  onDelete,
  componentName = '',
  subLevel = '',
  canDelete = false
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const handleSelect = (selectedIndex) => {
    setActiveIndex(selectedIndex);
  };

  const handlePrevious = () => {
    setActiveIndex((prev) => (prev === 0 ? files.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === files.length - 1 ? 0 : prev + 1));
  };

  const handleDelete = () => {
    if (onDelete && activeIndex >= 0) {
      onDelete(activeIndex);
      // Move to previous image if available
      if (activeIndex > 0) {
        setActiveIndex(activeIndex - 1);
      } else if (files.length > 1) {
        setActiveIndex(0);
      } else {
        onHide(); // Close modal if no files left
      }
    }
  };

  const downloadFile = (url, fileName) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'evidence_file';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAllFiles = () => {
    fileURLs.forEach((url, index) => {
      const fileName = files[index]?.split('/').pop() || `evidence_${index + 1}`;
      setTimeout(() => {
        downloadFile(url, fileName);
      }, index * 500); // Delay to prevent browser blocking multiple downloads
    });
  };

  const getFileType = (fileName) => {
    // Remove query parameters from URL (for signed S3 URLs)
    const cleanFileName = fileName.split('?')[0];
    const ext = cleanFileName.split('.').pop().toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return 'image';
    return 'other';
  };

  const renderFilePreview = (url, fileName, index) => {
    const fileType = getFileType(fileName);

    if (fileType === 'image') {
      return (
        <div className="image-preview">
          <img src={url} alt={fileName} />
        </div>
      );
    } else if (fileType === 'pdf') {
      return (
        <div className="pdf-preview">
          <div className="pdf-icon">📄</div>
          <div className="pdf-info">
            <h5>PDF Document</h5>
            <p>{fileName}</p>
            <Button
              variant="primary"
              onClick={() => window.open(url, '_blank')}
            >
              Open PDF in New Tab
            </Button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="file-preview">
          <div className="file-icon">📎</div>
          <div className="file-info">
            <h5>File</h5>
            <p>{fileName}</p>
            <Button
              variant="primary"
              onClick={() => downloadFile(url, fileName)}
            >
              Download File
            </Button>
          </div>
        </div>
      );
    }
  };

  if (!files || files.length === 0) {
    return (
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>No Evidence Files</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-center">No evidence files have been uploaded yet.</p>
        </Modal.Body>
      </Modal>
    );
  }

  // Extract clean filename from file path or URL (removing query params)
  const getCleanFileName = (filePath) => {
    if (!filePath) return 'File';
    const cleanPath = filePath.split('?')[0]; // Remove query params
    return cleanPath.split('/').pop() || 'File';
  };

  const currentFileName = getCleanFileName(files[activeIndex]);
  const currentURL = fileURLs[activeIndex];

  return (
    <Modal show={show} onHide={onHide} size="xl" centered className="evidence-gallery-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          {componentName} {subLevel ? `- ${subLevel}` : ''} Evidence ({files.length} {files.length === 1 ? 'file' : 'files'})
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="gallery-container">
          {/* Main Preview Area */}
          <div className="preview-area">
            <div className="navigation-controls">
              <Button
                variant="outline-secondary"
                onClick={handlePrevious}
                disabled={files.length <= 1}
                className="nav-btn"
              >
                ‹
              </Button>
              <div className="preview-content">
                {renderFilePreview(currentURL, currentFileName, activeIndex)}
              </div>
              <Button
                variant="outline-secondary"
                onClick={handleNext}
                disabled={files.length <= 1}
                className="nav-btn"
              >
                ›
              </Button>
            </div>

            <div className="file-info-bar">
              <div className="file-name-display">
                <strong>{currentFileName}</strong>
                <span className="file-counter"> (File {activeIndex + 1} of {files.length})</span>
              </div>
            </div>
          </div>

          {/* Thumbnail Strip */}
          {files.length > 1 && (
            <div className="thumbnail-strip">
              {files.map((file, index) => {
                const fileName = getCleanFileName(file);
                const fileType = getFileType(file);
                const isActive = index === activeIndex;

                return (
                  <div
                    key={index}
                    className={`thumbnail ${isActive ? 'active' : ''}`}
                    onClick={() => setActiveIndex(index)}
                  >
                    {fileType === 'image' && fileURLs[index] && (
                      <img src={fileURLs[index]} alt={fileName} />
                    )}
                    {fileType === 'pdf' && <div className="thumb-icon">📄</div>}
                    {fileType === 'other' && <div className="thumb-icon">📎</div>}
                    <div className="thumb-number">{index + 1}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="footer-actions">
          {/* <div className="left-actions">
            <Button
              variant="outline-primary"
              onClick={() => downloadFile(currentURL, currentFileName)}
            >
              Download Current
            </Button>
            {files.length > 1 && (
              <Button
                variant="outline-primary"
                onClick={downloadAllFiles}
              >
                Download All ({files.length})
              </Button>
            )}
          </div> */}
          <div className="right-actions">
            {canDelete && (
              <Button
                variant="outline-danger"
                onClick={handleDelete}
              >
                Delete This File
              </Button>
            )}
            <Button variant="secondary" onClick={onHide}>
              Close
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default EvidenceGalleryModal;
